import { useEffect, useRef, useState, useMemo } from "react";
import { Tooltip } from '../../../../components/Tooltip';
import { showToast } from '../../../../helper/ShowToast';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import TonalReportChart from "./components/TonalReportChart";
import { useMixAnalysisReportByTrack } from "../../../../queries/urstudio/mixanalyst.query";
import { useLocation, useNavigate } from "react-router-dom";
import MusicLoader from "../../../../helper/MusicLoader";

const RangeWithTooltip = ({ value, min, max, onChange, ariaLabel }) => {
    const [showTooltip, setShowTooltip] = useState(false);

    const TRACK_HEIGHT = 56; // h-14 = 56px
    const KNOB_SIZE = 12;    // h-3 = 12px

    const percent = (value - min) / (max - min);                    // 0 to 1
    const bottomPx = percent * (TRACK_HEIGHT - KNOB_SIZE);          // 0px to 44px

    return (
        <div className="relative h-14 w-6">
            {/* Background track */}
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-full w-2 rounded-full mx-auto bg-gradient-to-t from-[#10B981] via-[#06b6d4] to-[#ec4899]" />
            </div>

            {/* Knob */}
            <div
                className="absolute left-1/2 -translate-x-1/2"
                style={{ bottom: `${bottomPx}px`, position: 'absolute' }}
            >
                <div className="h-3 w-3 rounded-full bg-pink-500 border-2 border-white" />
            </div>

            {/* Tooltip */}
            {showTooltip && (
                <div
                    className="absolute left-6 bg-theme_clr1 border border-white/30 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-20 pointer-events-none"
                    style={{ bottom: `${bottomPx}px`, transform: 'translateY(50%)' }}
                >
                    {value}
                </div>
            )}

            {/* Invisible range input */}
            <input
                type="range"
                min={min}
                max={max}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                onTouchStart={() => setShowTooltip(true)}
                onTouchEnd={() => setShowTooltip(false)}
                aria-label={ariaLabel}
                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
            />
        </div>
    );
};

export function AnalysisReport() {
    const location = useLocation();
    const navigate = useNavigate()
    const reportRef = useRef(null);
    const params = new URLSearchParams(location.search);
    const reportId = params.get('id')
    const { data, isLoading, isError } = useMixAnalysisReportByTrack(reportId);

    // Integrated loudness visual control (0-100)
    const [loudnessRange, setLoudnessRange] = useState(0);
    const [peakRange, setPeakRange] = useState(0);

    const [expanded, setExpanded] = useState([]);

    const payload = data?.mix_analysis_report?.payload;
    const summary = payload?.summary;

    const sampleRate = payload?.sample_rate ?? '—';
    const bitDepth = payload?.bit_depth ?? '—';
    const clipping = payload?.clipping ?? '—';
    const monoCompat = payload?.mono_compatible != null ? (payload.mono_compatible ? 'Yes' : 'No') : '—';
    const intLoudness = payload?.integrated_loudness_lufs != null ? payload.integrated_loudness_lufs.toFixed(1) : '—';
    const truePeak = payload?.peak_loudness_dbfs ?? '—';
    const phaseIssues = payload?.phase_issues != null ? (payload.phase_issues ? 'Yes' : 'No') : '—';
    const stereoField = payload?.stereo_field
        ? payload.stereo_field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        : '—';

    useEffect(() => {
        if (payload?.integrated_loudness_lufs != null) {
            setLoudnessRange(payload.integrated_loudness_lufs);
        }
        if (payload?.peak_loudness_dbfs != null) {
            setPeakRange(payload.peak_loudness_dbfs);
        }
    }, [payload]);

    const suggestions = useMemo(() => {
        const text = summary?.summary ?? '';
        const blocks = text.split(/\n\n/).filter(line => /^\d+\./.test(line.trim()));

        const titleMap = {
            'dynamic range': 'Dynamic Range',
            'tonal': 'Tonal Profile',
            'EQ': 'Tonal Profile',
            'tonal profile': 'Tonal Profile',
            'clipping': 'Clipping',
            'stereo field': 'Stereo Field',
            'mono compatibility': 'Mono Compatibility',
            'loudness': 'Loudness',
            'loud': 'Loudness',
            'louder': 'Loudness',
            'too quiet': 'Loudness',
            'too soft': 'Loudness',
            'too low': 'Loudness',
            'integrated loudness': 'Loudness'
        };

        const extractTitle = (text) => {
            const matchedKey = Object.keys(titleMap).find(k =>
                text.toLowerCase().includes(k.toLowerCase())
            );
            return matchedKey ? titleMap[matchedKey] : null;
        };

        const deriveFallbackTitle = (content) => {
            // Strip leading number, bullet symbols, and common filler words
            const cleaned = content
                .replace(/^\d+\.\s*/, '')
                .replace(/^(the|a|an|your|this|there|is|are|it)\s+/i, '')
                .trim();

            // Capitalise first 3–5 meaningful words as a fallback title
            const words = cleaned.split(/\s+/).slice(0, 4);
            return words
                .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                .join(' ');
        };

        return blocks.map((block, index) => {
            const content = block.replace(/^\d+\.\s*/, '').trim();
            const sentences = content.split(/(?<=\.)\s+/);
            const short = sentences[0] ?? content;
            const details = sentences.slice(1).join(' ');

            // 1st pass: try to match title from short sentence
            const titleFromShort = extractTitle(short);

            // 2nd pass: if not found, try to match from full content
            const titleFromContent = !titleFromShort ? extractTitle(content) : null;

            // 3rd pass: if still not found, derive a readable title from content words
            const title = titleFromShort ?? titleFromContent ?? deriveFallbackTitle(content);

            return {
                id: index + 1,
                title,
                short,
                details,
            };
        });
    }, [summary]);

    const toggle = (id) => {
        setExpanded((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const shareReportLink = () => {
        showToast('info', 'Track url copied to clipboard')
        const url = `${window.location.origin}${'/admin/urstudio/analysis-report'}?id=${reportId}`;
        navigator.clipboard.writeText(url);
    }

    const downloadPDF = async () => {
        const element = reportRef.current;
        if (!element) return;

        // Hide pdf-exclude elements
        const excluded = element.querySelectorAll('.pdf-exclude');
        excluded.forEach(el => el.style.display = 'none');

        // Fix gradient text headers — html2canvas can't render bg-clip-text
        const gradientTexts = element.querySelectorAll('[class*="bg-clip-text"]');
        gradientTexts.forEach(el => {
            el.style.webkitTextFillColor = '#ffffff';
            el.style.backgroundImage = 'none';
            el.style.color = '#ffffff';
        });

        // Expand all accordions so full content is captured
        const prevExpanded = [...expanded];
        setExpanded(suggestions.map(s => s.id));

        // Wait for accordion re-render
        await new Promise(resolve => setTimeout(resolve, 150));

        try {
            showToast('info', 'Generating PDF...');

            const canvas = await html2canvas(element, {
                scale: 1.5,                  // lower than 2 = smaller file size
                useCORS: true,
                backgroundColor: '#00067e',
                logging: false,
                imageTimeout: 0,
                removeContainer: true,
            });

            // JPEG at 0.8 quality instead of PNG — drastically reduces file size
            const imgData = canvas.toDataURL('image/jpeg', 0.8);

            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
                compress: true,
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const totalHeightMM = (canvas.height * pdfWidth) / canvas.width;

            let yOffset = 0;
            let pageNum = 0;

            while (yOffset < totalHeightMM) {
                if (pageNum > 0) pdf.addPage();

                // Fill entire page with report background color before placing image
                pdf.setFillColor(0, 6, 110); // #00067e in RGB
                pdf.rect(0, 0, pdfWidth, pdfHeight, 'F');

                pdf.addImage(imgData, 'JPEG', 0, -yOffset, pdfWidth, totalHeightMM);
                yOffset += pdfHeight;
                pageNum++;
            }

            const filename = `${data?.projectName?.replace(/\.[^.]+$/, '') ?? 'mix-report'}_MixReport.pdf`;

            pdf.save(filename);
            showToast('success', 'PDF downloaded successfully');

        } catch (err) {
            console.error('PDF generation error:', err);
            showToast('error', 'Failed to generate PDF');
        } finally {
            // Restore everything regardless of success/error
            excluded.forEach(el => el.style.display = '');
            gradientTexts.forEach(el => {
                el.style.webkitTextFillColor = '';
                el.style.backgroundImage = '';
                el.style.color = '';
            });
            setExpanded(prevExpanded);
        }
    };

    const handleNewUpload = () => {
        navigate('/admin/urstudio/mix-analyst');
    }
    return (
        <div className="analysis-report container mb-4">
            <section className="bg-theme_clr1 text-white rounded-2xl mt-4 p-4 lg:p-5" ref={reportRef}>
                {isLoading ? (
                    <div className="h-80">
                        <MusicLoader />
                        <div className="text-center text-gray-400 py-6">Loading your analysis report...</div>
                    </div>
                ) : (
                    <>
                        <div className="bg-transparent border border-white text-white flex items-center gap-2 rounded-lg mb-2 px-3 py-2">
                            <i className="bi bi-music-note-beamed text-base"></i>
                            <span className="text-sm">{(data?.filename || data?.projectName) ?? 'TRACK'}</span>
                        </div>
                        <div className="mix-report-wrapper mt-4">
                            <div className="heading-wrapper font-roboto flex flex-wrap items-center justify-between gap-4 mb-4">
                                <div className="left-section">
                                    <div className="bg-gradient-to-r from-white/70 to-theme_clr4 to-[30%] bg-clip-text text-transparent text-xl mb-1">{data?.isMastered ? "Master" : "Mix"} Report <i className="bi bi-check-lg"></i></div>
                                    <div className="heading-desc font-roboto text-white/50 text-[13px]">Detailed analysis of your track's technical quality</div>
                                </div>
                                <div className="right-section flex flex-wrap gap-4 pdf-exclude">
                                    <button onClick={handleNewUpload} className="bg-theme_clr2 hover:bg-theme_clr4 border border-white/40 rounded-full text-white/75 hover:text-white transition-all px-4 py-2">
                                        New Upload
                                    </button>
                                    <button onClick={shareReportLink} className="bg-theme_clr1 hover:bg-theme_clr2 border border-white/40 rounded-full text-white/75 hover:text-white transition-all px-4 py-2">
                                        <i className="bi bi-share-fill mr-2"></i> Share
                                    </button>
                                    <button onClick={downloadPDF} className="bg-theme_clr1 hover:bg-theme_clr2 border border-white/40 rounded-full text-white/75 hover:text-white transition-all px-4 py-2">
                                        <i className="bi bi-floppy2-fill mr-2"></i> Save
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 md:gap-4 mb-4">
                                <div className="info-card border border-white text-theme_clr2 rounded-xl p-4">
                                    <div className="label text-sm mb-1">Sample Rate
                                        <Tooltip
                                            title='Sample Rate'
                                            text="The number of audio samples captured per second in digital audio, affecting the frequency range and audio quality. Higher sample rates result in more accurate sound reproduction.
                This should be at least CD-Quality i.e. 44100 Hz."
                                            style={{ bg: "bg-white", text: "text-theme_clr1", width: "w-64" }}
                                            position='right'
                                        >
                                            <i className="bi bi-info-circle ml-1"></i>
                                        </Tooltip>
                                    </div>
                                    <div className="value text-xl">{sampleRate}Hz</div>
                                </div>
                                <div className="info-card border border-white text-theme_clr2 rounded-xl p-4">
                                    <div className="label text-sm mb-1">Bit Depth
                                        <Tooltip
                                            title='Bit Depth'
                                            text="The number of bits used to represent each audio sample, affecting the dynamic range and precision of digital audio.
                Higher bit depth allows for more accurate sound reproduction. You bit depth should be at least 16 bit, but can be 24 bit."
                                            style={{ bg: "bg-white", text: "text-theme_clr1", width: "w-64" }}
                                            position='right'
                                        >
                                            <i className="bi bi-info-circle ml-1"></i>
                                        </Tooltip>
                                    </div>
                                    <div className="value text-xl">{bitDepth}bit</div>
                                </div>
                                <div className="info-card border border-white text-theme_clr2 rounded-xl p-4">
                                    <div className="label text-sm mb-1">Clipping
                                        <Tooltip
                                            title='Clipping'
                                            text="Distortion caused by an audio signal exceeding the system's limit, resulting in a harsh or distorted sound. Prevent by managing volume and using audio tools."
                                            style={{ bg: "bg-white", text: "text-theme_clr1", width: "w-64" }}
                                            position='right'
                                        >
                                            <i className="bi bi-info-circle ml-1"></i>
                                        </Tooltip>
                                    </div>
                                    <div className="value text-xl">{clipping}</div>
                                </div>
                                <div className="info-card border border-white text-theme_clr2 rounded-xl p-4">
                                    <div className="label text-sm mb-1">Mono Compatibility
                                        <Tooltip
                                            title='Mono Compatibility'
                                            text="The ability of a stereo audio mix to maintain balance, phase coherence, and sound quality when collapsed to a single (mono) channel. Important for ensuring consistent playback on various systems."
                                            style={{ bg: "bg-white", text: "text-theme_clr1", width: "w-64" }}
                                            position='right'
                                        >
                                            <i className="bi bi-info-circle ml-1"></i>
                                        </Tooltip>
                                    </div>
                                    <div className="value text-xl">{monoCompat}</div>
                                </div>
                                <div className="info-card border border-white text-white rounded-xl p-4 flex items-center justify-between">
                                    <div className="flex-1 pr-4">
                                        <div className="label text-sm mb-1">Integrated Loudness
                                            <Tooltip
                                                title='Integrated Loudness'
                                                text="A measure of the average perceived loudness of an audio signal over its entire duration. It gives a single value that represents the overall loudness level of the audio content, and is used to ensure consistent loudness across different programs and content."
                                                style={{ bg: "bg-white", text: "text-theme_clr1", width: "w-64" }}
                                                position='right'
                                            >
                                                <i className="bi bi-info-circle text-white/50 ml-1"></i>
                                            </Tooltip>
                                        </div>
                                        <div className="value text-xl">{intLoudness} LUFS</div>
                                    </div>

                                    <div className="flex-shrink-0 flex items-center">
                                        <RangeWithTooltip
                                            value={intLoudness}
                                            min={-30}
                                            max={0}
                                            onChange={setLoudnessRange}
                                            ariaLabel="Integrated loudness"
                                        />
                                    </div>
                                </div>
                                <div className="info-card border border-white text-white rounded-xl p-4 flex items-center justify-between">
                                    <div className="flex-1 pr-4">
                                        <div className="label text-sm mb-1">True Peak
                                            <Tooltip
                                                title='True Peak'
                                                text="True Peak Loudness measures the highest amplitude in an audio waveform, considering intersample peaks that regular peak measurements might miss. Expressed in dBTP or dBFS, it helps prevent clipping and distortion, ensuring optimal audio quality and compatibility with different playback systems."
                                                style={{ bg: "bg-white", text: "text-theme_clr1", width: "w-64" }}
                                                position='right'
                                            >
                                                <i className="bi bi-info-circle text-white/50 ml-1"></i>
                                            </Tooltip>
                                        </div>
                                        <div className="value text-xl">{truePeak} dBFS</div>
                                    </div>

                                    <div className="flex-shrink-0 flex items-center">
                                        <RangeWithTooltip
                                            value={truePeak}
                                            min={-30}
                                            max={0}
                                            onChange={setPeakRange}
                                            ariaLabel="True Peak"
                                        />
                                    </div>
                                </div>
                                <div className="info-card border border-white text-white rounded-xl p-4">
                                    <div className="label text-sm mb-1">Phase Issues
                                        <Tooltip
                                            title='Phase Issues'
                                            text="Timing differences between audio waveforms, causing cancellation or reinforcement of frequencies, which may lead to a weak or hollow sound. Resolve by adjusting the timing or using phase correction tools."
                                            style={{ bg: "bg-white", text: "text-theme_clr1", width: "w-64" }}
                                            position='right'
                                        >
                                            <i className="bi bi-info-circle text-white/50 ml-1"></i>
                                        </Tooltip>
                                    </div>
                                    <div className="value text-xl">{phaseIssues}</div>
                                </div>
                                <div className="info-card border border-white text-white rounded-xl p-4">
                                    <div className="label text-sm mb-1">Stereo Field
                                        <Tooltip
                                            title='Stereo Field'
                                            text="The perceived width and depth of an audio mix, created by differences in level, timing, and frequency content between the left and right channels. Enhances listener immersion and spatial experience."
                                            style={{ bg: "bg-white", text: "text-theme_clr1", width: "w-64" }}
                                            position='right'
                                        >
                                            <i className="bi bi-info-circle text-white/50 ml-1"></i>
                                        </Tooltip>
                                    </div>
                                    <div className="value text-xl">{stereoField}</div>
                                </div>
                            </div>
                            <div className="info-card border border-white text-white rounded-xl p-4">
                                <div className="chart-wrapper">
                                    <div className="w-full mx-auto rounded-lg">
                                        <div className="flex items-center gap-2 mb-4">
                                            <h2 className="text-white text-sm font-normal">Tonal Profile</h2>
                                            <Tooltip
                                                title='Tonal Profile'
                                                text="This section analyses your mix by breaking it down into four frequency bands: Bass (20-250Hz), Low-mids (250-2kHz), High-mids (2-8kHz), and Highs (8-20kHz). We measure the sound energy in each range to assess the overall tonal balance. Each frequency range is evaluated and given a result: Optimal: Your mix sounds well-balanced in this frequency range, and no adjustments are needed. Too Low:
                                    This frequency range is quieter than ideal. Too High: This frequency range is louder than ideal."
                                                style={{ bg: "bg-white", text: "text-theme_clr1", width: "w-64" }}
                                                position='right'
                                            >
                                                <i className="bi bi-info-circle text-white/50"></i>
                                            </Tooltip>
                                        </div>
                                    </div>
                                    <TonalReportChart tonalProfile={payload?.tonal_profile} />
                                </div>
                            </div>
                        </div>

                        <div className="suggested-changes-wrapper font-inter mt-4">
                            <div className="heading-wrapper mb-4">
                                <div className="text-white text-xl mb-1">Suggested Changes</div>
                                <div className="heading-desc font-roboto text-white/50 text-sm">These are suggestions based on our analysis. Always listen critically and trust your ears.</div>
                            </div>

                            {suggestions.length > 0 ? (
                                <div className="flex flex-col gap-4">
                                    {suggestions.map((s) => {
                                        const isOpen = expanded.includes(s.id);
                                        return (
                                            <div key={s.id} className="info-card border border-white text-theme_clr2 rounded-xl p-4">
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="w-full">
                                                        <div className="label font-medium text-red-500 text-sm mb-1">{s.title}</div>
                                                        <div className="value text-white text-base">{s.short}</div>
                                                        {isOpen && (
                                                            <div className="mt-3 text-white/75 text-sm">{s.details}</div>
                                                        )}
                                                    </div>
                                                    <div className="flex-shrink-0 flex items-start">
                                                        <button onClick={() => toggle(s.id)} aria-expanded={isOpen} className="text-theme_clr2 hover:text-white transition-transform transform">
                                                            <i className={`bi bi-chevron-down text-xl ${isOpen ? 'rotate-180' : ''}`}></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-white/50 text-sm">No suggestions available.</div>
                            )}
                        </div>
                    </>
                )}
            </section>
        </div>
    )
}